import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Card, Grid, Typography, useMediaQuery, Drawer, IconButton, Tooltip } from '@mui/material';
import Layout from 'src/components/layout/Layout';
import { useTranslation } from 'react-i18next';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { addEvent, editEventPair, resetError } from 'src/redux/actions/Scheduler';
import toast from 'react-hot-toast';
import { api } from 'src/api/endpoints';
import AddEventDialogMinute from './components/AddEventDialogMinute';
import CalendarGridMinute from './components/CalendarGridMinute';
import SchedulersList from '../scheduler/components/SchedulersList';
import EditScheduleGroupDialog from '../scheduler/components/EditScheduleGroupDialog';
import ScreensDrawer from '../scheduler/components/ScreensDrawer';
import AttachSchedulerDialog from '../scheduler/components/AttachSchedulerDialog';
import DetachSchedulerDialog from '../scheduler/components/DetachSchedulerDialog';
import EventDetailsDialog from '../scheduler/components/EventDetailsDialog';
import EventsList from '../scheduler/components/EventsList';
import CalendarGridControl from '../scheduler/components/CalendarGridControl';

// Lightweight minute-precision scheduler leveraging existing redux scheduler slice
// Does not modify original scheduler; uses minute-based grid & solid event spans

const SchedulerMinute = ({ socketRef }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { schedulers, schedulerError } = useSelector(state => state.Scheduler);
  const isNarrowViewport = useMediaQuery('(max-width:1100px)');

  const [currentEditingScheduler, setCurrentEditingScheduler] = useState({ open: false, data: null });
  const [currentViewingScheduler, setCurrentViewingScheduler] = useState('');
  const [openScreensDrawer, setOpenScreensDrawer] = useState(false);
  const [attachSchedulerDialog, setAttachSchedulerDialog] = useState({ open: false, screen: null });
  const [detachSchedulerDialog, setdetachSchedulerDialog] = useState({ open: false, screen: null });
  const [currentSchedulerEventsListOpen, setCurrentSchedulerEventsListOpen] = useState({ open: false, id: '' });
  const [currentViewingEvent, setCurrentViewingEvent] = useState({ open: false, eventStart: null, eventStop: null });
  const [openAddEventDialog, setOpenAddEventDialog] = useState({ open: false, date: null });
  const [isFullView, setIsFullView] = useState(false);
  const [calendarSettings, setCalendarSettings] = useState({ startDate: null, endDate: null });
  const [openSchedulersDrawer, setOpenSchedulersDrawer] = useState(false);

  const isPrinting = useRef(false);

  // Auto-enable full-screen mode for narrow viewports
  useEffect(() => {
    setIsFullView(isNarrowViewport);
  }, [isNarrowViewport]);

  useEffect(() => {
    const startD = new Date();
    const endD = new Date();
    startD.setDate(startD.getDate() - startD.getDay());
    endD.setDate(endD.getDate() + (6 - endD.getDay()));

    setCalendarSettings({
      startDate: startD,
      endDate: endD
    });

    // Lock scrolling to 0 only for wider viewports
    if (!isNarrowViewport) {
      const preventScroll = () => {
        window.scrollTo(0, 0);
      };

      // Prevent initial scroll
      window.scrollTo(0, 0);
      
      // Lock scroll position
      window.addEventListener('scroll', preventScroll);
      
      // Cleanup
      return () => {
        window.removeEventListener('scroll', preventScroll);
      };
    }
  }, [isNarrowViewport]);

  useEffect(() => {
    if(schedulerError){
      switch(schedulerError) {
        case 'serverErr': toast.error(t('views.scheduler.serverErr')); break;
        case 'invalidObId': toast.error(t('views.scheduler.invalidObId')); break;
        case 'duplicate': toast.error(t('views.scheduler.duplicateError')); break;
        case 'eventsOverlaping': toast.error(t('views.scheduler.eventsOverlapingErr')); break;
        default: break;
      }
      dispatch(resetError());
    }
  }, [schedulerError]);

  const syncScreensByMac = async (macs) => {
    try {
      for await (const screen of macs) {
        await socketRef?.current?.emit('sync', { detail:{ mac:screen }});
      }
    } catch(err) {
      console.error('Failed to sync screens', err);
    }
  };

  const getPairAndOpenDetails = async (clickedEvent) => {
    try {
      const response = await api.get(`/scheduler/getEventPair/${clickedEvent.pair}`);
      console.log(response.data);
      setCurrentViewingEvent({
        open: true,
        eventStart: response.data?.pair?.find(p => p.type === 'start'),
        eventStop: response.data?.pair?.find(p => p.type === 'stop')
      });
    } catch(err) {
      toast.error(t('views.scheduler.errorGettingPair'));
    }
  };

  const handleAttachSchedulerDialogOpen = (screen) => {
    setAttachSchedulerDialog({ open: true, screen: screen });
  };

  const handleAddEvent = async (data) => {
    if(data.mode === 'add') {
      const resp = await dispatch(addEvent({ group:currentViewingScheduler, ...data }));
      if(resp) syncScreensByMac(resp.data.screensAttachedToScheduler.map(s => s.mac));
    } else {
      const resp = await dispatch(editEventPair({ pairId:data.pairId, contentId:data.contentId, start:data.whenStart, stop:data.whenStop, contentType:data.contentType }));
      if(resp) syncScreensByMac(resp.data.screensAttachedToScheduler.map(s => s.mac));
    }
  };

  const handleFullViewToggle = () => {
    if (isNarrowViewport) {
      // On narrow viewport - always open schedulers drawer
      setOpenSchedulersDrawer(true);
    } else {
      // Toggle full view on wider viewport
      setIsFullView(!isFullView);
    }
  };

  const currentSchedulerData = schedulers.find(s => s._id === currentViewingScheduler);

  return (
    <Layout title={t('views.scheduler.title') + ' (Minute)'}>
      <Box component="main" sx={{
        display: "flex",
        flexDirection: "column",
        gap: "15px",
        px: 3
      }}>
        <Box sx={{
          mt: 2
        }}>
          <Button endIcon={<ArrowForwardIcon/>} onClick={() => setOpenScreensDrawer(true)} sx={{
            mr: 2
          }}>{t("views.scheduler.showScreens")}</Button>
          <Button endIcon={isFullView ? <FullscreenExitIcon/> : <FullscreenIcon/>} onClick={handleFullViewToggle}>{t("views.scheduler.fullScreeen")}</Button>
        </Box>
        <Grid container spacing={2} sx={{
          width: "100%"
        }}>
          {!isFullView && (
            <SchedulersList setOpenScreensDrawer={setOpenScreensDrawer} gridSize={3} setCurrentScheduler={setCurrentViewingScheduler} currentScheduler={currentViewingScheduler} setCurrentEditingScheduler={setCurrentEditingScheduler} />
          )}
          <Grid item xs={isFullView ? 12 : 9}>
            <Card variant="outlined" sx={{
              p: 2,
              width: "100%",
              border: `1px solid ${currentSchedulerData?.color}`
            }}>
              {currentViewingScheduler && (
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}>
                  <Button size='small' onClick={() => setOpenAddEventDialog({...openAddEventDialog, open: true})} variant="outlined" endIcon={<AddIcon />}>{t("views.scheduler.addEvent")}</Button>
                  <Button size='small' onClick={() => setCurrentSchedulerEventsListOpen({open: true, id: currentViewingScheduler})} variant="outlined" endIcon={<FormatListBulletedIcon />}>{t("views.scheduler.eventsList")}</Button>
                </Box>
              )}
              <CalendarGridControl calendarSettings={calendarSettings} setCalendarSettings={setCalendarSettings} />
              <CalendarGridMinute themeColor={schedulers.find(sch => sch._id === currentViewingScheduler)?.color} getPairAndOpenDetails={getPairAndOpenDetails} startDate={calendarSettings.startDate} endDate={calendarSettings.endDate} events={currentSchedulerData} setDay={setCalendarSettings} />
            </Card>
          </Grid>
        </Grid>
        <EditScheduleGroupDialog setCurrentViewingScheduler={setCurrentViewingScheduler} open={currentEditingScheduler.open} onClose={() => setCurrentEditingScheduler({open: false, data: null})} value={currentEditingScheduler?.data?.name} color={currentEditingScheduler?.data?.color} id={currentEditingScheduler?.data?.id} schedulerData={currentEditingScheduler.data} />
        <ScreensDrawer open={openScreensDrawer} onClose={() => setOpenScreensDrawer(false)} attachScheduler={handleAttachSchedulerDialogOpen} setdetachSchedulerDialog={setdetachSchedulerDialog} />
        <AttachSchedulerDialog open={attachSchedulerDialog.open} syncScreensByMac={syncScreensByMac} onClose={() => setAttachSchedulerDialog({open: false, screen: null})} screenData={attachSchedulerDialog.screen} />
        <AddEventDialogMinute open={openAddEventDialog.open} onClose={() => setOpenAddEventDialog({date: null, open: false})} presetDate={openAddEventDialog.date} onSave={handleAddEvent} currentScheduler={currentSchedulerData} />
        <EventDetailsDialog socketRef={socketRef} open={currentViewingEvent.open} onEditEvent={(pair) => setOpenAddEventDialog({open: true, date: pair})} onClose={() => setCurrentViewingEvent({
          ...currentViewingEvent,
          open: false,
        })} eventStart={currentViewingEvent.eventStart} eventStop={currentViewingEvent.eventStop} />
        <EventsList openEvent={(pair) => getPairAndOpenDetails({pair})} openAddEventDialog={() => setOpenAddEventDialog({...openAddEventDialog, open: true})} open={currentSchedulerEventsListOpen.open} onClose={() => setCurrentSchedulerEventsListOpen({id: '', open: false})} schedulerId={currentSchedulerEventsListOpen.id} />
        <DetachSchedulerDialog open={detachSchedulerDialog.open} onClose={() => setdetachSchedulerDialog({open: false, screen: null})} screenData={detachSchedulerDialog.screen} syncScreensByMac={syncScreensByMac} />
        
        {/* Schedulers Drawer for narrow viewports */}
        <Drawer anchor="left" open={openSchedulersDrawer} onClose={() => setOpenSchedulersDrawer(false)}>
          <Box sx={{ width: 320 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #2d3748' }}>
              <Typography variant="h6">{t("views.scheduler.schedulers") || "Schedulers"}</Typography>
              <IconButton onClick={() => setOpenSchedulersDrawer(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ p: 2 }}>
              <Card sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%"
              }} variant="outlined">
                <Box sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  p:2,
                  backgroundColor: theme => theme.palette.mode === 'dark' ? '#1C2536' : '#F8F9FA',
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t("views.scheduler.schedulers") || "Select Scheduler"}</Typography>
                </Box>
                <Box sx={{
                  display: "flex",
                  flexDirection: "column",
                }}>
                  {schedulers.map(scheduler => (
                    <Box key={scheduler._id} sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1,
                      px:2,
                      justifyContent: "space-between",
                      borderBottom: "1px solid #2d3748",
                      cursor: 'pointer'
                    }} onClick={() => {
                      setCurrentViewingScheduler(scheduler._id);
                      setOpenSchedulersDrawer(false);
                    }}>
                      <Box>
                        <Tooltip title={scheduler.name.length > 25 ? scheduler.name : ""}>
                          <Typography>
                            {scheduler.name.length > 25 ? `${scheduler.name.slice(0, 25)}...` : scheduler.name}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        borderRadius: '50%', 
                        backgroundColor: scheduler.color,
                        border: currentViewingScheduler === scheduler._id ? '2px solid white' : 'none',
                        boxShadow: currentViewingScheduler === scheduler._id ? '0 0 0 2px ' + scheduler.color : 'none'
                      }} />
                    </Box>
                  ))}
                </Box>
              </Card>
            </Box>
          </Box>
        </Drawer>
      </Box>
    </Layout>
  );
};

export default SchedulerMinute;
